/**
 * AI API Endpoints
 * All endpoints for interacting with Wealfi Financial Coach
 */

import apiClient from '../client';
import type {
  MessageResponse,
  Conversation,
  FinancialProfile,
  FinancialProfileResponse,
  FinancialProfileGetResponse,
  FinancialStateResponse,
  UsageResponse,
  RecentUsageResponse,
  WelcomeResponse,
  OnboardingStep,
  OnboardingStepData,
  OnboardingStepResponse,
  DiagnosticActionResponse,
  FinancialGoalsResponse,
} from '../types';

export const aiApi = {
  /**
   * POST /ai/message
   * Send a message to the AI Financial Coach and receive a response
   * The backend determines which AI provider to use based on configuration
   */
  sendMessage: async (message: string): Promise<MessageResponse> => {
    const response = await apiClient.post<MessageResponse>('/ai/message', {
      message,
    });
    return response.data;
  },

  /**
   * GET /ai/conversation
   * Get or create the AI Coach conversation for the current user
   * Returns the conversation with full message history
   */
  getConversation: async (): Promise<Conversation> => {
    const response = await apiClient.get<Conversation>('/ai/conversation');
    return response.data;
  },

  /**
   * POST /ai/welcome
   * Get welcome message from AI Coach (doesn't use AI tokens)
   */
  getWelcomeMessage: async (): Promise<WelcomeResponse> => {
    const response = await apiClient.post<WelcomeResponse>('/ai/welcome');
    return response.data;
  },

  /**
   * PUT /ai/financial_profile
   * Update the user's financial profile
   */
  updateFinancialProfile: async (
    profile: Partial<FinancialProfile>
  ): Promise<FinancialProfileResponse> => {
    const response = await apiClient.put<FinancialProfileResponse>(
      '/ai/financial_profile',
      profile
    );
    return response.data;
  },

  /**
   * GET /ai/financial_profile
   * Retrieve the user's current financial profile
   */
  getFinancialProfile: async (): Promise<FinancialProfileGetResponse> => {
    const response = await apiClient.get<FinancialProfileGetResponse>(
      '/ai/financial_profile'
    );
    return response.data;
  },

  /**
   * GET /ai/financial_state
   * Get financial state summary with progression and next steps
   * Used for Profile tab (Profil Financier section) and Diagnostic tab
   */
  getFinancialState: async (): Promise<FinancialStateResponse> => {
    const response = await apiClient.get<FinancialStateResponse>(
      '/ai/financial_state'
    );
    return response.data;
  },

  /**
   * GET /ai/usage
   * Get AI usage statistics for the current user
   */
  getUsage: async (
    period: 'all_time' | 'today' | 'this_week' | 'this_month' = 'all_time'
  ): Promise<UsageResponse> => {
    const response = await apiClient.get<UsageResponse>(
      `/ai/usage?period=${period}`
    );
    return response.data;
  },

  /**
   * GET /ai/usage/recent
   * Get recent AI usage records with detailed information
   */
  getRecentUsage: async (limit: number = 10): Promise<RecentUsageResponse> => {
    const response = await apiClient.get<RecentUsageResponse>(
      `/ai/usage/recent?limit=${limit}`
    );
    return response.data;
  },

  /**
   * GET /ai/onboarding/status
   * Get current AI onboarding status and progress
   */
  getOnboardingStatus: async (): Promise<OnboardingStep> => {
    const response = await apiClient.get<OnboardingStep>('/ai/onboarding/status');
    return response.data;
  },

  /**
   * POST /ai/onboarding/step
   * Submit onboarding step data and advance to next step
   */
  submitOnboardingStep: async (
    step: string,
    data: OnboardingStepData
  ): Promise<OnboardingStepResponse> => {
    const response = await apiClient.post<OnboardingStepResponse>(
      '/ai/onboarding/step',
      { step, data }
    );
    return response.data;
  },

  /**
   * POST /ai/onboarding/diagnostic-action
   * Submit diagnostic action choice and complete onboarding
   */
  submitDiagnosticAction: async (action: string): Promise<DiagnosticActionResponse> => {
    const response = await apiClient.post<DiagnosticActionResponse>(
      '/ai/onboarding/diagnostic-action',
      { action }
    );
    return response.data;
  },

  /**
   * POST /financial_goals
   * Confirm and save a financial goal
   */
  confirmFinancialGoal: async (financialGoalData: {
    title: string;
    target_amount?: number;
    deadline?: string;
    monthly_effort?: number;
    financial_goal_type?: string;
    message_id?: string;
  }) => {
    const response = await apiClient.post('/financial_goals', financialGoalData);
    return response.data;
  },

  /**
   * GET /financial_goals
   * Get all confirmed financial goals for the current user
   * @param status - Filter by status: 'active', 'completed', 'archived', 'all' (default: 'all')
   */
  getFinancialGoals: async (status: 'active' | 'completed' | 'archived' | 'all' = 'all'): Promise<FinancialGoalsResponse> => {
    const response = await apiClient.get<FinancialGoalsResponse>('/financial_goals', {
      params: { status },
    });
    return response.data;
  },

  /**
   * PATCH /financial_goals/:id/update_progress
   * Update progress (current_amount) for a financial goal
   */
  updateGoalProgress: async (goalId: string, currentAmount: number): Promise<any> => {
    const response = await apiClient.patch(`/financial_goals/${goalId}/update_progress`, {
      current_amount: currentAmount,
    });
    return response.data;
  },

  /**
   * PATCH /financial_goals/:id/complete_step
   * Mark a roadmap sub-goal as completed and activate the next one
   */
  completeRoadmapStep: async (stepId: string): Promise<{
    success: boolean;
    completed_step: {
      id: string;
      title: string;
      position: number;
    };
    next_step?: {
      id: string;
      title: string;
      position: number;
    };
    roadmap_progress: number;
    is_roadmap_complete: boolean;
    message: string;
  }> => {
    const response = await apiClient.patch(`/financial_goals/${stepId}/complete_step`);
    return response.data;
  },

  /**
   * PATCH /financial_goals/:id/complete_action_step
   * Mark an action step as completed for investment goals
   */
  completeActionStep: async (goalId: string, stepNumber: number): Promise<{
    success: boolean;
    completed_step: {
      step: number;
      title: string;
    };
    next_step?: {
      step: number;
      title: string;
    };
    progress: {
      completed: number;
      total: number;
      percentage: number;
    };
    is_all_steps_complete: boolean;
    financial_goal: any;
    message: string;
  }> => {
    const response = await apiClient.patch(`/financial_goals/${goalId}/complete_action_step`, {
      step_number: stepNumber,
    });
    return response.data;
  },
};

